import Helper from '@ember/component/helper';
import { type Registry as Services, service } from '@ember/service';
import { type Hotel, Market, type Ota } from 'frontend-utils/models';
import type { RoomType } from 'frontend-utils/models/roomtype';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import getCompsetTranslationKey from 'frontend-utils/utils/get-compset-translationkey';
import type {
  MealTypeValues,
  MembershipType,
} from 'lumen/utils/constants/general-constants';
import {
  CUSTOM_ROOMTYPE_PREFIX,
  MEALTYPE_TRANSLATION_KEYS,
  MEMBERSHIPTYPE_TRANSLATION_KEYS,
  RATETYPES,
} from 'lumen/utils/constants/general-constants';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';

interface Filter {
  bar?: boolean;
  brand?: number[];
  compset?: string | null;
  flexible?: boolean;
  los?: number;
  management_type?: string[];
  mealtype?: MealTypeValues | null;
  membershiptype?: MembershipType;
  ota: string;
  persons?: number;
  platform?: Platform;
  rateParam?: string;
  rate_type?: number;
  ratechange_days?: number[];
  room?: RoomType;
  subtype?: 'major' | 'other';
}

interface DisplayRateParityFiltersSignature {
  Args: {
    Positional: [Filter, Hotel | Market];
  };
  Return: string;
}

export default class DisplayRateParityFiltersHelper extends Helper<DisplayRateParityFiltersSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  compute([filters, hotelOrMarket]: [Filter | null, Hotel | Market]): string {
    const options = [];

    if (
      filters === null ||
      filters === undefined ||
      Object.keys(filters).length === 0
    ) {
      return '';
    }

    if (filters.rateParam !== undefined && filters.rateParam !== null) {
      mapRateParamToAPIParams(filters.rateParam, filters);
    }

    // We need to check on the API properties for the scheduled report filters (not rateParam)
    if (
      filters.rate_type !== undefined &&
      filters.rate_type === RATETYPES.THIRD_PARTY
    ) {
      options.push(this.intl.t('rates.third_party'));
    } else if (filters.bar !== undefined && filters.bar) {
      options.push(this.intl.t('rates.bar_short'));
    } else if (filters.flexible !== undefined && filters.flexible) {
      // This check must be done after the bar check because flexible is also set to true for bar
      options.push(this.intl.t('rates.flexible'));
    } else if (filters.bar !== undefined && !filters.bar) {
      options.push(this.intl.t('rates.lowest'));
    }

    let otaObject = null;
    if (filters.ota !== undefined && filters.ota !== null) {
      otaObject = this.store.peekRecord<Ota>('ota', filters.ota);
      if (otaObject) {
        options.push(otaObject.name);
      }
    }

    if (
      this.access.hasFeature('rates.mobile') &&
      otaObject !== null &&
      otaObject.hasMobileFilter &&
      filters.platform !== undefined &&
      filters.platform > PLATFORMS.PLATFORM_UNSPECIFIED
    ) {
      options.push(this.intl.t(`rates.platform.${filters.platform}`));
    }

    if (
      filters.membershiptype !== undefined &&
      filters.membershiptype !== null &&
      otaObject?.hasMemberFilter
    ) {
      const membershiptype =
        MEMBERSHIPTYPE_TRANSLATION_KEYS[filters.membershiptype];
      if (membershiptype) {
        options.push(this.intl.t(membershiptype));
      }
    }

    if (
      filters.brand !== undefined &&
      hotelOrMarket instanceof Market &&
      hotelOrMarket.brands
    ) {
      const brands = filters.brand
        .map((brand) => {
          return hotelOrMarket.brands.find((b) => b.id === brand)?.name;
        })
        .filter((x) => x);
      if (brands.length) {
        options.push(brands.join(', '));
      }
    }

    if (
      filters.management_type !== undefined &&
      hotelOrMarket instanceof Market &&
      hotelOrMarket.managementTypes
    ) {
      const managementTypes = filters.management_type
        .map((type) => {
          const match = hotelOrMarket.managementTypes.find(
            (t) => t.id === type,
          );
          return match ? this.intl.t(match.name) : null;
        })
        .filter((x) => x);
      if (managementTypes.length) {
        options.push(managementTypes.join(', '));
      }
    }

    if (filters.los !== undefined) {
      options.push(this.intl.t('rates.nights', { count: filters.los }));
    }

    if (filters.persons !== undefined) {
      options.push(
        this.intl.t('rates.persons_selection', { count: filters.persons }),
      );
    }

    if (filters.room !== undefined && filters.room !== null) {
      if (filters.room?.startsWith(CUSTOM_ROOMTYPE_PREFIX)) {
        // Fall back to a default message when the custom room type is not found
        let customRoomType = this.intl.t('reports.room_type_filter');

        const roomTypes =
          this.hotelAndMarketService.hotel?.hotelinfo?.getRoomTypesForOTA(
            filters.ota,
          );

        if (roomTypes?.length) {
          customRoomType =
            roomTypes.find((rt) => rt.id === filters.room)?.name ??
            customRoomType;
        }

        options.push(customRoomType);
      } else if (filters.room) {
        options.push(this.intl.t(`rates.roomtypes.${filters.room}`));
      } else {
        options.push(this.intl.t('reports.room_type_filter'));
      }
    }

    if (filters.mealtype !== undefined && filters.mealtype !== null) {
      const mealPlan = MEALTYPE_TRANSLATION_KEYS[filters.mealtype];
      if (mealPlan) {
        options.push(this.intl.t(mealPlan));
      }
    }

    if (
      filters.ratechange_days !== undefined &&
      filters.ratechange_days !== null
    ) {
      let offsets = '';

      if (
        filters.ratechange_days.length === 1 &&
        filters.ratechange_days[0] === 1
      ) {
        offsets += this.intl.t('general.daysago', { count: 1 });
      } else {
        const vsString = this.intl.t('rates.compare.days_ago', {
          count: filters.ratechange_days.join(', '),
        });
        offsets += this.intl.t('general.versus.option', { option: vsString });
      }

      options.push(`${this.intl.t('rates.changes.ratechanges')}: ${offsets}`);
    }

    if (filters.subtype !== undefined) {
      if (filters.subtype === 'other') {
        options.push(this.intl.t('reports.parity.other_otas'));
      } else {
        options.push(this.intl.t('reports.parity.major_otas'));
      }
    }

    if (filters.compset !== undefined && filters.compset !== null) {
      options.push(this.intl.t(getCompsetTranslationKey(filters.compset)));
    }

    // \u00A0 is unicode alternative for non-breaking space. Allows us to have more than once space before and after the separator.
    return options.join('\u00A0\u00A0-\u00A0\u00A0');
  }
}
